import { FlowStep, FunnelNode, StepConfig, StepType } from '../types';
import { STEP_TYPE_LABELS } from '../constants/stepStyles';

const FRIENDLY_PAGE_NAMES: Partial<Record<StepType, string>> = {
  landing: 'Landing Page',
  checkout: 'Checkout Page',
  offer: 'Offer Page',
  thankyou: 'Thank You Page',
  custom: 'Custom Page',
  error: 'Error Page',
  external: 'External Page',
  storefront_shopify: 'Shopify Page',
  storefront_woocommerce: 'WooCommerce Page',
};

/**
 * Generate a friendly step label like "Checkout Page", "Checkout Page 2", etc.
 * Counts existing steps of the same type to determine the suffix number.
 */
export function generateFriendlyStepLabel(stepType: StepType, existingSteps: FlowStep[] = []): string {
  const baseName = FRIENDLY_PAGE_NAMES[stepType] || `${STEP_TYPE_LABELS[stepType] || stepType} Page`;
  const sameTypeCount = existingSteps.filter((s) => s?.type === stepType).length;
  if (sameTypeCount === 0) return baseName;
  return `${baseName} ${sameTypeCount + 1}`;
}

// Position calculation constants
const POSITION_CONSTRAINTS = {
  MAX_X: 3200,
  MAX_Y: 2640,
  MIN_X: -400,
  MIN_Y: -220,
  STEP_WIDTH: 400,
  STEP_HEIGHT: 220,
} as const;

/**
 * Generate slug from name with optional unique suffix
 */
export const generateSlug = (name: string, addSuffix: boolean = false): string => {
  const baseSlug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (addSuffix) {
    // Add short unique suffix (4 chars from timestamp)
    const suffix = Date.now().toString(36).slice(-4);
    return `${baseSlug}-${suffix}`;
  }

  return baseSlug;
};

/**
 * Helper to create a new step with smart default name
 */
export const createNewStep = (col: number, row: number, label?: string, stepType?: StepType, existingSteps?: FlowStep[]): FlowStep => {
  const safeCol = typeof col === 'number' && isFinite(col) ? Math.floor(col) : 0;
  const safeRow = typeof row === 'number' && isFinite(row) ? Math.floor(row) : 0;

  const resolvedType = stepType || 'landing';
  const defaultLabel = label || generateFriendlyStepLabel(resolvedType, existingSteps);

  return {
    id: `step_${Date.now()}`,
    label: defaultLabel,
    type: resolvedType,
    position: { row: safeRow, col: safeCol },
    // No pagePath seed: an empty string is falsy, so it read as "configured"
    // to nothing while masking that the step still has no page (TAG-1946).
    // The path is written when a design is picked or built.
    config: {},
    conditions: {},
    connections: [],
  };
};

/**
 * Finds the best available position for a new node.
 * Positions the node to the right of the rightmost existing node.
 * 
 * @param existingItems - Array of FlowStep (with flowPosition) OR FunnelNode (with position)
 */
export function findAvailablePosition(existingItems: (FlowStep | FunnelNode)[]): { x: number; y: number } {
  const { STEP_WIDTH } = POSITION_CONSTRAINTS;
  const MARGIN = 80; // Spacing between nodes
  const GRID_X = STEP_WIDTH + MARGIN;

  // If no nodes exist, start at origin
  if (existingItems.length === 0) {
    const position = { x: 0, y: 0 };
    return position;
  }

  // Extract positions from items (handles both FlowStep.flowPosition and FunnelNode.position)
  const nodesWithPosition = existingItems
    .map((item) => {
      // Try both position properties
      const pos = ('position' in item && item.position) || ('flowPosition' in item && item.flowPosition);

      // Check if pos has x and y (not row/col)
      if (pos && 'x' in pos && 'y' in pos && typeof pos.x === 'number' && typeof pos.y === 'number') {
        return {
          id: item.id,
          x: pos.x,
          y: pos.y,
        };
      }
      return null;
    })
    .filter((n): n is { id: string; x: number; y: number } => n !== null);

  // If no nodes have positions, start at origin
  if (nodesWithPosition.length === 0) {
    const position = { x: 0, y: 0 };
    return position;
  }

  // Find the rightmost node (max X coordinate)
  const rightmostNode = nodesWithPosition.reduce((rightmost, current) => {
    return current.x > rightmost.x ? current : rightmost;
  }, nodesWithPosition[0]);

  // Position new node to the right of the rightmost node
  const newX = rightmostNode.x + GRID_X;
  const newY = rightmostNode.y; // Same Y as rightmost node

  return { x: newX, y: newY };
}

/**
 * Get steps grouped by column
 */
export const getStepsByColumn = (steps: FlowStep[]): { [col: number]: FlowStep[] } => {
  const byColumn: { [col: number]: FlowStep[] } = {};
  steps.forEach((step) => {
    // Defensive check: ensure step has position
    if (!step.position) {
      return;
    }
    const col = step.position.col;
    // Validate col is a valid number
    if (typeof col !== 'number' || !isFinite(col) || col < 0 || col > 999) {
      return;
    }
    if (!byColumn[col]) byColumn[col] = [];
    byColumn[col].push(step);
  });
  return byColumn;
};

/**
 * Determine winner steps (highest conversion rate per column)
 */
export const getWinnerStepIds = (stepsByColumn: { [col: number]: FlowStep[] }): Set<string> => {
  const winners = new Set<string>();

  // Find winner in each column
  Object.values(stepsByColumn).forEach((stepsInCol) => {
    const stepsWithMetrics = stepsInCol.filter((s) => s.metrics?.progressionRate);
    if (stepsWithMetrics.length < 2) return; // Need at least 2 alternatives to have a winner

    const winner = stepsWithMetrics.reduce((prev, current) =>
      current.metrics!.progressionRate! > prev.metrics!.progressionRate! ? current : prev,
    );

    winners.add(winner.id);
  });

  return winners;
};

/** A page of a deployment's manifest, as `plugins.getDeploymentDetails` returns it. */
interface DeploymentPage {
  path?: string;
  isDefault?: boolean;
}

/**
 * Which page path a deployment serves.
 *
 * Read from the deployment itself, NOT from the step-type catalog
 * (`getAvailableInstancesForStepType`): that one filters manifest pages by the
 * step's type, so it answers nothing whenever the deployed page's type differs
 * from the step's — which is exactly what a page built from scratch in Studio
 * produces (the blank seed is a `landing` page, on an `offer` step). Verified in
 * a browser against a real Studio deploy: the catalog returned 0 entries for
 * `offer` while the deployment carried the page all along.
 *
 * A multi-page plugin serves every page from one instance, so `instanceId` alone
 * cannot pick between them (TAG-2021 hit the same ambiguity from the
 * design-picker side); the default page breaks that tie. With several pages and
 * no default, answer undefined rather than write a path that silently decides
 * where the step publicly serves.
 */
export const resolveDeployedPagePath = (pages: DeploymentPage[] | undefined): string | undefined => {
  if (!pages?.length) return undefined;
  if (pages.length === 1) return pages[0].path || undefined;
  return pages.find((page) => page.isDefault)?.path || undefined;
};

/**
 * The step config a Studio save produces.
 *
 * Writes the deployed path to `metadata.internalPath` only, never to `pagePath`.
 * `instanceId` + `internalPath` is enough for the server's isStepConfigComplete
 * (and is the shape 4672 of the 4881 Studio steps in prod already carry), while
 * `pagePath` is the FIRST field resolveStepPath reads — ahead of `config.path`.
 * Writing it here would override the distinct path a funnel template seeded per
 * step, collapsing sibling offer steps onto one path, and would read downstream
 * as a deliberate merchant remap. A saved design must not move a step's URL.
 *
 * Every write is guarded by the existing value so an absent payload field never
 * clears a pointer the step already had (TAG-1946).
 */
export const buildStudioSaveConfig = (
  baseConfig: StepConfig | undefined,
  deployment: {
    pluginId?: string;
    deploymentId?: string;
    instanceId?: string;
    instanceVersion?: string;
    pagePath?: string;
  },
): StepConfig => {
  const internalPath = baseConfig?.metadata?.internalPath || deployment.pagePath;

  return {
    ...baseConfig,
    pluginId: deployment.pluginId || baseConfig?.pluginId,
    instanceId: deployment.instanceId || baseConfig?.instanceId,
    instanceVersion: deployment.instanceVersion || baseConfig?.instanceVersion,
    metadata: {
      ...baseConfig?.metadata,
      editorType: 'studio',
      pluginId: deployment.pluginId || baseConfig?.metadata?.pluginId || baseConfig?.pluginId,
      deploymentId: deployment.deploymentId || baseConfig?.metadata?.deploymentId,
      ...(internalPath ? { internalPath } : {}),
    },
  };
};
